import { useRef, useState } from 'react';
import clsx from 'clsx';

import { pad2 } from '@/shared/lib/pad2';

function split(value: string) {
  const [h = '', m = ''] = String(value ?? '').split(':');
  return { h, m };
}

/** Минуты «реальной» оценки — из значения вида «HH:MM». */
function minutesOf(value: string) {
  const { h, m } = split(value);
  return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
}

function hm(minutes: number) {
  const t = Math.round(minutes);
  return `${pad2(Math.floor(t / 60))}:${pad2(t % 60)}`;
}

/**
 * «Время на выполнение»: BEST (×0.8) — REAL — WORST (×1.3).
 * REAL вводится вручную, пока нет чек-листов; как только у пунктов появилось
 * время, поле становится нередактируемым и рядом стоит «· из чек-листов».
 */
export function RHoursField({
  value,
  onChange,
  disabled,
  readOnly = false,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
}) {
  const [draft, setDraft] = useState<{ h: string; m: string } | null>(null);
  const minutesRef = useRef<HTMLInputElement | null>(null);

  const shown = draft ?? split(value);
  const real = minutesOf(value);
  const locked = readOnly || disabled;

  const commit = (h: string, m: string) => {
    if (locked) {
      return;
    }
    setDraft({ h, m });
    onChange(`${h || '0'}:${m || '0'}`);
  };

  const inputClass = clsx(
    'border-none bg-transparent p-0 text-[12px] font-semibold outline-none',
    readOnly ? 'text-[rgba(20,30,49,0.55)]' : 'text-[#141E31]',
    disabled && 'cursor-not-allowed opacity-50',
    readOnly && 'cursor-default'
  );

  return (
    <span className="col-span-full flex min-w-0 flex-nowrap items-center gap-[10px] min-[620px]:gap-[18px]">
      <span className="shrink-0 whitespace-nowrap text-[11px] text-[rgba(20,30,49,0.7)] min-[620px]:text-[12px]">
        Время на выполнение
        {readOnly ? (
          <span className="text-[11px] text-[rgba(20,30,49,0.45)]">
            {' '}
            · из чек-листов
          </span>
        ) : null}
      </span>
      <span className="hidden min-w-0 flex-1 min-[620px]:block" />

      <span
        className="flex shrink-0 items-baseline gap-[6px]"
        title="Best — оптимистичная оценка (Real × 0.8)"
      >
        <span className="text-[10px] font-bold tracking-[0.4px] text-[rgba(20,30,49,0.4)]">
          BEST
        </span>
        <span className="text-[12px] font-semibold tabular-nums text-[#57B792]">
          {hm(real * 0.8)}
        </span>
      </span>

      <span className="flex shrink-0 items-baseline gap-[6px] min-[620px]:mx-[18px]">
        <span className="shrink-0 text-[10px] font-bold tracking-[0.4px] text-[rgba(20,30,49,0.4)]">
          REAL
        </span>
        <span className="flex shrink-0 items-baseline gap-[2px] border-b border-[rgba(59,77,127,0.3)] pb-[4px] pt-[3px]">
          <input
            value={shown.h}
            disabled={disabled}
            readOnly={readOnly}
            placeholder="00"
            onFocus={(e) => !readOnly && e.currentTarget.select()}
            onChange={(e) =>
              commit(e.target.value.replace(/\D/g, '').slice(0, 3), shown.m)
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                minutesRef.current?.focus();
                minutesRef.current?.select();
              }
            }}
            onBlur={() => setDraft(null)}
            className={clsx(inputClass, 'w-[20px] text-right')}
            aria-label="Часы"
          />
          <span
            className={clsx(
              'text-[12px] font-semibold',
              readOnly ? 'text-[rgba(20,30,49,0.55)]' : 'text-[#141E31]'
            )}
          >
            :
          </span>
          <input
            ref={minutesRef}
            value={shown.m}
            disabled={disabled}
            readOnly={readOnly}
            placeholder="00"
            onFocus={(e) => !readOnly && e.currentTarget.select()}
            onChange={(e) =>
              commit(shown.h, e.target.value.replace(/\D/g, '').slice(0, 2))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            onBlur={() => setDraft(null)}
            className={clsx(inputClass, 'w-[26px] text-left')}
            aria-label="Минуты"
          />
        </span>
      </span>

      <span
        className="flex shrink-0 items-baseline gap-[6px]"
        title="Worst — пессимистичная оценка (Real × 1.3)"
      >
        <span className="text-[10px] font-bold tracking-[0.4px] text-[rgba(20,30,49,0.4)]">
          WORST
        </span>
        <span className="text-[12px] font-semibold tabular-nums text-[#E59D02]">
          {hm(real * 1.3)}
        </span>
      </span>
    </span>
  );
}
